import { useRef } from 'react';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion';
import { useReducedMotion } from '@/lib/motion';

/**
 * The signature hero object — an isometric "glass box" the market pours into and
 * a concentrated book comes out of. Adapted from the /investor Glass Box motif
 * (BlackBoxProofHook) and made interactive: it tilts toward the pointer
 * (mouse-parallax), raw tickers fall into the lid, and vetted winners pop out
 * below. Illustrative only — decorative tickers, not fund performance.
 *
 * Reduced motion: no tilt, no falling/popping tickers, static beam.
 */

// Raw universe symbols falling in (illustrative).
const FALL_TICKERS = [
  { s: 'NVDA', x: 22, delay: 0 },
  { s: 'AAPL', x: 60, delay: 1.1 },
  { s: 'MSFT', x: 40, delay: 2.2 },
  { s: 'AMZN', x: 78, delay: 3.3 },
  { s: 'META', x: 32, delay: 4.4 },
  { s: 'GOOGL', x: 68, delay: 5.5 },
];

// Vetted winners popping out (illustrative, decorative).
const POP_TICKERS = [
  { s: 'NVDA', p: '+18%', x: 24, by: 22, delay: 0.6 },
  { s: 'AAPL', p: '+12%', x: 74, by: 10, delay: 1.7 },
  { s: 'AVGO', p: '+15%', x: 44, by: 30, delay: 2.9 },
  { s: 'MSFT', p: '+9%', x: 66, by: 20, delay: 4.0 },
];

export function GlassCube() {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), {
    stiffness: 120,
    damping: 18,
  });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), {
    stiffness: 120,
    damping: 18,
  });

  function onMove(e: React.MouseEvent<HTMLDivElement>) {
    if (reduced) return;
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  }
  function onLeave() {
    mx.set(0);
    my.set(0);
  }

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="mx-auto flex w-full max-w-md flex-col items-center gap-6 [perspective:1100px]"
    >
      <span className="mkt-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
        S&amp;P 500 · in
      </span>

      <motion.div
        className="mkt-float relative w-full"
        style={reduced ? undefined : { rotateX: rotX, rotateY: rotY }}
      >
        {/* Raw tickers falling into the lid */}
        {!reduced && (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[52%] overflow-visible">
            {FALL_TICKERS.map((t) => (
              <motion.span
                key={t.s}
                className="mkt-mono absolute -translate-x-1/2 rounded-[3px] border border-white/15 bg-[#05070d] px-1.5 py-1 text-[10px] font-semibold tracking-wide text-white/90 shadow-[0_2px_10px_rgba(0,0,0,0.7)]"
                style={{ left: `${t.x}%` }}
                initial={{ y: '-75%', opacity: 0 }}
                animate={{ y: ['-75%', '118%'], opacity: [0, 1, 1, 0] }}
                transition={{
                  duration: 3.8,
                  delay: t.delay,
                  repeat: Infinity,
                  repeatDelay: 1.6,
                  ease: 'easeIn',
                  times: [0, 0.14, 0.72, 1],
                }}
              >
                {t.s}
              </motion.span>
            ))}
          </div>
        )}

        <svg
          viewBox="0 78 300 260"
          className="h-auto w-full drop-shadow-[0_30px_60px_rgba(0,0,0,0.5)]"
          role="img"
          aria-label="An illustration of the NovaQuantis engine: the market flows into a transparent glass box and a concentrated portfolio comes out."
        >
          <defs>
            <linearGradient id="mcFaceL" x1="0" y1="0" x2="0.6" y2="1">
              <stop offset="0" stopColor="#15273f" />
              <stop offset="1" stopColor="#091322" />
            </linearGradient>
            <linearGradient id="mcFaceR" x1="1" y1="0" x2="0.4" y2="1">
              <stop offset="0" stopColor="#0d1a2c" />
              <stop offset="1" stopColor="#060d17" />
            </linearGradient>
            <radialGradient id="mcWell" cx="0.5" cy="0.32" r="0.75">
              <stop offset="0" stopColor="#1f6f93" />
              <stop offset="0.45" stopColor="#0c2336" />
              <stop offset="1" stopColor="#05101c" />
            </radialGradient>
            <linearGradient id="mcBeam" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0" stopColor="#3cc8fa" stopOpacity="0.65" />
              <stop offset="0.45" stopColor="#f7941d" stopOpacity="0.32" />
              <stop offset="1" stopColor="#f7941d" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="mcLid" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#5acdfa" stopOpacity="0.32" />
              <stop offset="1" stopColor="#122640" stopOpacity="0.55" />
            </linearGradient>
            <radialGradient id="mcOut" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0" stopColor="#3dff85" stopOpacity="0.55" />
              <stop offset="1" stopColor="#3dff85" stopOpacity="0" />
            </radialGradient>
            <filter id="mcGlow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="2.3" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="mcSoft" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="6" />
            </filter>
          </defs>

          {/* Inner well + extraction beam */}
          <polygon points="150,188 236,220 150,252 64,220" fill="url(#mcWell)" />
          <polygon
            className="mkt-beam"
            points="124,234 176,234 240,84 60,84"
            fill="url(#mcBeam)"
            filter="url(#mcSoft)"
          />

          {/* Back rim (far lip) — sits behind the bull so the horns read in front */}
          <path
            d="M64,220 L150,188 L236,220"
            fill="none"
            stroke="#7fe3ff"
            strokeWidth="1.6"
            filter="url(#mcGlow)"
          />

          {/* Bull — in front of the back rim, but behind the front faces below */}
          <image
            href="/assets/logo-bull-trim.png"
            x="86"
            y="140"
            width="128"
            height="148"
            opacity="0.95"
          />

          {/* Front faces — opaque, so they occlude the bull's lower body inside the box */}
          <polygon
            points="64,220 150,252 150,310 64,278"
            fill="url(#mcFaceL)"
            stroke="#3fd0ff"
            strokeWidth="1.4"
            filter="url(#mcGlow)"
          />
          <polygon
            points="150,252 236,220 236,278 150,310"
            fill="url(#mcFaceR)"
            stroke="#3fd0ff"
            strokeWidth="1.4"
            filter="url(#mcGlow)"
          />

          {/* Circuitry on the faces */}
          <g stroke="#54d6ff" strokeWidth="1" fill="none" opacity="0.7">
            <path d="M78,234 L78,252 L96,262 L96,282" />
            <circle cx="78" cy="234" r="2.2" fill="#54d6ff" stroke="none" />
            <circle cx="96" cy="282" r="2.2" fill="#54d6ff" stroke="none" />
            <path d="M210,232 L210,250 L194,260" />
            <circle cx="210" cy="232" r="2.2" fill="#ffb547" stroke="none" />
          </g>

          {/* Front rim (near lip) — bright opening edge, drawn on top of the faces */}
          <path
            d="M64,220 L150,252 L236,220"
            fill="none"
            stroke="#7fe3ff"
            strokeWidth="1.6"
            filter="url(#mcGlow)"
          />

          {/* Lid */}
          <g>
            <polygon
              points="150,92 232,120 150,148 68,120"
              fill="url(#mcLid)"
              stroke="#7fe3ff"
              strokeWidth="1.5"
              filter="url(#mcGlow)"
            />
            <line x1="150" y1="148" x2="150" y2="188" stroke="#3fd0ff" strokeWidth="1.1" opacity="0.5" />
          </g>

          {/* Output portal glow */}
          <ellipse cx="150" cy="318" rx="52" ry="9" fill="url(#mcOut)" />
        </svg>

        {/* Vetted winners popping out below the box */}
        {!reduced && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[30%] overflow-visible">
            {POP_TICKERS.map((t) => (
              <motion.span
                key={t.s}
                className="absolute flex -translate-x-1/2 items-center gap-1 rounded-full border border-[rgba(61,255,133,0.4)] bg-[rgba(61,255,133,0.1)] px-2 py-0.5 shadow-[0_0_14px_rgba(61,255,133,0.25)] backdrop-blur-sm"
                style={{ left: `${t.x}%`, bottom: `${t.by}%` }}
                initial={{ y: 10, opacity: 0, scale: 0.82 }}
                animate={{ y: [10, -16], opacity: [0, 1, 1, 0], scale: [0.82, 1.05, 1, 0.95] }}
                transition={{
                  duration: 2.2,
                  delay: t.delay,
                  repeat: Infinity,
                  repeatDelay: 1.9,
                  ease: 'easeOut',
                  times: [0, 0.2, 0.75, 1],
                }}
              >
                <span className="mkt-mono text-[10px] font-semibold text-white/90">{t.s}</span>
                <span className="mkt-mono text-[10px] font-bold text-[#3dff85]">{t.p}</span>
              </motion.span>
            ))}
          </div>
        )}
      </motion.div>

      <span className="mkt-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--nq-gold-amber)]">
        20–30 holdings · out
      </span>
    </div>
  );
}
